import { nanoid } from 'nanoid/non-secure';
import type {
  AnimatableProperty,
  Animation,
  AnimationEffect,
  AnimationKeyframe,
  AnimationTrack,
} from '@adflow/types';
import {
  DEFAULT_SLIDE_DISTANCE,
  generatePresetKeyframes,
  getAnimationPreset,
  isAnimationPresetId,
  resolvePresetParams,
  toStoredPresetParams,
  type AnimationPresetDefinition,
} from './presets';

export const DEFAULT_ANIMATION_TIMELINE = {
  duration: 3,
  fps: 30,
  loop: false,
} as const;

export class PresetRoleError extends Error {
  readonly presetId: string;
  readonly effectId: string;

  constructor(presetId: string, effectId: string) {
    super(`Preset "${presetId}" does not match the role of effect "${effectId}"`);
    this.name = 'PresetRoleError';
    this.presetId = presetId;
    this.effectId = effectId;
  }
}

export class ConflictingPresetError extends Error {
  readonly presetId: string;
  readonly targetId: string;

  constructor(presetId: string, targetId: string) {
    super(`Preset "${presetId}" conflicts with an effect on "${targetId}"`);
    this.name = 'ConflictingPresetError';
    this.presetId = presetId;
    this.targetId = targetId;
  }
}

export class DuplicatePresetError extends Error {
  readonly presetId: string;
  readonly targetId: string;

  constructor(presetId: string, targetId: string) {
    super(`Preset "${presetId}" is already applied to "${targetId}"`);
    this.name = 'DuplicatePresetError';
    this.presetId = presetId;
    this.targetId = targetId;
  }
}

export type ApplyAnimationEffectInput = {
  targetId: string;
  presetId: string;
  params?: Record<string, number | string | boolean> | null;
  effectId?: string;
};

function emptyAnimation(): Animation {
  return {
    timeline: { ...DEFAULT_ANIMATION_TIMELINE },
    tracks: [],
    effects: [],
  };
}

function sortKeyframes(keyframes: AnimationKeyframe[]): AnimationKeyframe[] {
  return [...keyframes].sort((a, b) => a.t - b.t);
}

function upsertTrack(
  tracks: AnimationTrack[],
  targetId: string,
  property: AnimatableProperty,
  effectId: string,
  keyframes: AnimationKeyframe[],
): AnimationTrack[] {
  const index = tracks.findIndex(
    (track) => track.targetId === targetId && track.property === property,
  );
  if (index === -1) {
    return [...tracks, { targetId, property, keyframes: sortKeyframes(keyframes) }];
  }

  const existing = tracks[index];
  const merged = sortKeyframes([
    ...existing.keyframes.filter((keyframe) => keyframe.effectId !== effectId),
    ...keyframes,
  ]);
  return tracks.map((track, i) => (i === index ? { ...track, keyframes: merged } : track));
}

function removeEffectFromTracks(tracks: AnimationTrack[], effectId: string): AnimationTrack[] {
  return tracks
    .map((track) => ({
      ...track,
      keyframes: track.keyframes.filter((keyframe) => keyframe.effectId !== effectId),
    }))
    .filter((track) => track.keyframes.length > 0);
}

export type PresetSelectionBlock = 'duplicate' | 'conflict';

export function getPresetSelectionBlock(
  effects: readonly AnimationEffect[],
  targetId: string,
  presetId: string,
  ignoreEffectId?: string,
): PresetSelectionBlock | null {
  const preset = getAnimationPreset(presetId);
  const relevant = effects.filter(
    (effect) => effect.targetId === targetId && effect.id !== ignoreEffectId,
  );
  if (relevant.some((effect) => effect.presetId === preset.id)) {
    return 'duplicate';
  }
  const conflicts = relevant.some((effect) => {
    if (!isAnimationPresetId(effect.presetId)) {
      return false;
    }
    const other = getAnimationPreset(effect.presetId);
    return other.phase === preset.phase && other.property === preset.property;
  });
  return conflicts ? 'conflict' : null;
}

function findConflictingEffect(
  effects: AnimationEffect[],
  targetId: string,
  preset: AnimationPresetDefinition,
): AnimationEffect | undefined {
  return effects.find((effect) => {
    if (
      effect.targetId !== targetId ||
      effect.presetId === preset.id ||
      !isAnimationPresetId(effect.presetId)
    ) {
      return false;
    }
    const other = getAnimationPreset(effect.presetId);
    return other.phase === preset.phase && other.property === preset.property;
  });
}

export function applyAnimationEffect(
  animation: Animation | null,
  input: ApplyAnimationEffectInput,
): Animation {
  const preset = getAnimationPreset(input.presetId);
  const base = animation ?? emptyAnimation();
  const effects = [...(base.effects ?? [])];

  if (
    effects.some((effect) => effect.targetId === input.targetId && effect.presetId === preset.id)
  ) {
    throw new DuplicatePresetError(preset.id, input.targetId);
  }

  const conflict = findConflictingEffect(effects, input.targetId, preset);
  const effectsWithoutConflict = conflict
    ? effects.filter((effect) => effect.id !== conflict.id)
    : effects;
  let tracks = conflict ? removeEffectFromTracks(base.tracks, conflict.id) : [...base.tracks];

  const resolved = resolvePresetParams(preset, input.params);
  const storedParams = toStoredPresetParams(preset, resolved);
  const effectId = input.effectId ?? nanoid(11);
  const keyframes = generatePresetKeyframes(preset.id, resolved, effectId);

  tracks = upsertTrack(tracks, input.targetId, preset.property, effectId, keyframes);

  return {
    ...base,
    tracks,
    effects: [
      ...effectsWithoutConflict,
      {
        id: effectId,
        targetId: input.targetId,
        presetId: preset.id,
        params: storedParams,
      },
    ],
  };
}

export function removeAnimationEffect(animation: Animation, effectId: string): Animation | null {
  const effects = animation.effects ?? [];
  const remaining = effects.filter((effect) => effect.id !== effectId);
  if (remaining.length === effects.length) {
    throw new Error(`Animation effect "${effectId}" not found`);
  }
  if (remaining.length === 0) {
    return null;
  }
  return {
    ...animation,
    tracks: removeEffectFromTracks(animation.tracks, effectId),
    effects: remaining,
  };
}

export function updateAnimationEffect(
  animation: Animation,
  effectId: string,
  params: Record<string, number | string | boolean>,
): Animation {
  const effects = animation.effects ?? [];
  const existing = effects.find((effect) => effect.id === effectId);
  if (!existing) {
    throw new Error(`Animation effect "${effectId}" not found`);
  }

  const preset = getAnimationPreset(existing.presetId);
  const resolved = resolvePresetParams(preset, { ...existing.params, ...params });
  const storedParams = toStoredPresetParams(preset, resolved);
  const keyframes = generatePresetKeyframes(preset.id, resolved, existing.id);

  return {
    ...animation,
    tracks: upsertTrack(
      animation.tracks,
      existing.targetId,
      preset.property,
      existing.id,
      keyframes,
    ),
    effects: effects.map((effect) =>
      effect.id === effectId ? { ...effect, params: storedParams } : effect,
    ),
  };
}

export function changeAnimationEffectPreset(
  animation: Animation,
  effectId: string,
  presetId: string,
): Animation {
  const effects = animation.effects ?? [];
  const existing = effects.find((effect) => effect.id === effectId);
  if (!existing) {
    throw new Error(`Animation effect "${effectId}" not found`);
  }

  const currentPreset = getAnimationPreset(existing.presetId);
  const nextPreset = getAnimationPreset(presetId);
  if (nextPreset.id === currentPreset.id) {
    return animation;
  }
  if (nextPreset.phase !== currentPreset.phase) {
    throw new PresetRoleError(nextPreset.id, existing.id);
  }

  const block = getPresetSelectionBlock(effects, existing.targetId, nextPreset.id, existing.id);
  switch (block) {
    case 'duplicate':
      throw new DuplicatePresetError(nextPreset.id, existing.targetId);
    case 'conflict':
      throw new ConflictingPresetError(nextPreset.id, existing.targetId);
    case null:
      break;
    default: {
      const unreachable: never = block;
      throw new Error(`Unhandled preset selection block "${unreachable}"`);
    }
  }

  const currentParams = resolvePresetParams(currentPreset, existing.params);
  const nextInput: Record<string, number | string | boolean> = {
    duration: currentParams.duration,
    start: currentParams.start,
    easing: currentParams.easing,
  };
  if (nextPreset.group === 'slide') {
    nextInput.distance =
      currentPreset.group === 'slide'
        ? currentParams.distance ?? DEFAULT_SLIDE_DISTANCE
        : DEFAULT_SLIDE_DISTANCE;
  }

  const resolved = resolvePresetParams(nextPreset, nextInput);
  const storedParams = toStoredPresetParams(nextPreset, resolved);
  const keyframes = generatePresetKeyframes(nextPreset.id, resolved, existing.id);

  return {
    ...animation,
    tracks: upsertTrack(
      removeEffectFromTracks(animation.tracks, existing.id),
      existing.targetId,
      nextPreset.property,
      existing.id,
      keyframes,
    ),
    effects: effects.map((effect) =>
      effect.id === effectId
        ? { ...effect, presetId: nextPreset.id, params: storedParams }
        : effect,
    ),
  };
}
